'use client';

import React, { ReactNode, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { Box, IconButton, Typography, Drawer, useTheme, DrawerProps } from '@mui/material';
import { MAX_SCREEN_WIDTH } from '@/app/constants/layoutConfig';
import { CloseIconForBottomSheet } from '@/public/icon';
import CenteredBox from '@/app/base-ui/CenterBox';

interface DrawerCustomProps extends Omit<DrawerProps, 'title'> {
    open: boolean;
    onClose: () => void;
    title: string | ReactNode;
    hasBorderBottom?: boolean;
    children: React.ReactNode;
}

const DrawerCustom: React.FC<DrawerCustomProps> = ({ open, onClose, title, hasBorderBottom, children }) => {
    const theme = useTheme();
    if (typeof document === 'undefined') {
        return null;
    }

    return ReactDOM.createPortal(
        <Drawer
            anchor="bottom"
            open={open}
            aria-modal={true}
            onClose={onClose}
            disableScrollLock={true}
            PaperProps={{
                sx: {
                    borderRadius: '8px 8px 0 0',
                    maxWidth: MAX_SCREEN_WIDTH,
                    width: '100%',
                    margin: '0 auto',
                    paddingTop: hasBorderBottom ? 5 : 6.75,
                    display: 'flex',
                    background: theme.palette.background.default,
                },
            }}
            ModalProps={{
                sx: {
                    zIndex: 1301,
                },
                keepMounted: true,
                disableEnforceFocus: true,
            }}
        >
            {hasBorderBottom && (
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingX: 5, pb: 5, borderBottom: `1px solid ${theme.palette.grayscale.gray03}` }}>
                    {typeof title === 'string' ? <Typography variant="h2_18Bold">{title}</Typography> : title}
                    <IconButton
                        onClick={onClose}
                        sx={{ padding: 1 }}
                    >
                        <CloseIconForBottomSheet />
                    </IconButton>
                </Box>
            )}
            {!hasBorderBottom && (
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingX: 5 }}>
                    {typeof title === 'string' ? (
                        <Typography
                            variant="h2_12Medium"
                            sx={{ color: 'grayscale.gray05' }}
                        >
                            {title}
                        </Typography>
                    ) : (
                        title
                    )}
                    <IconButton
                        onClick={onClose}
                        sx={{ padding: 1 }}
                    >
                        <CloseIconForBottomSheet />
                    </IconButton>
                </Box>
            )}
            {children}
        </Drawer>,
        document.body
    );
};

export default DrawerCustom;
